import { createSelector } from 'reselect';

import * as LoadingState from '@blueberry/constants/loadingState';
import * as quizStatus from '@blueberry/constants/quizStatus';

export const types = {
  FETCH_POST: 'FETCH_POST',
  FETCH_POST_SUCCESS: 'FETCH_POST_SUCCESS',
  FETCH_POST_FAILURE: 'FETCH_POST_FAILURE',
  RESET_POST: 'RESET_POST',
  TOGGLE_FAVORITE: 'TOGGLE_FAVORITE',
  SHOW_LOGIN_DIALOG: 'SHOW_LOGIN_DIALOG',
  HIDE_LOGIN_DIALOG: 'HIDE_LOGIN_DIALOG',
  POST_EVENT: 'POST_EVENT',
  FETCH_QUIZ_HISTORY: 'FETCH_QUIZ_HISTORY',
  FETCH_QUIZ_HISTORY_SUCCESS: 'FETCH_QUIZ_HISTORY_SUCCESS',
  FETCH_QUIZ_HISTORY_FAILURE: 'FETCH_QUIZ_HISTORY_FAILURE',
  FETCH_NEXT_QUIZ: 'FETCH_NEXT_QUIZ',
  FETCH_NEXT_QUIZ_SUCCESS: 'FETCH_NEXT_QUIZ_SUCCESS',
  FETCH_NEXT_QUIZ_FAILURE: 'FETCH_NEXT_QUIZ_FAILURE',
  CHANGE_CONCEPT: 'CHANGE_CONCEPT',
  POST_QUIZ_ANSWER: 'POST_QUIZ_ANSWER',
  POST_QUIZ_ANSWER_SUCESS: 'POST_QUIZ_ANSWER_SUCCESS',
  CHANGE_QUIZ_INDEX: 'CHANGE_QUIZ_INDEX',
  CHANGE_QUIZ_INDEX_SUCCESS: 'CHANGE_QUIZ_INDEX_SUCCESS',
};

export const actions = {
  fetchPost: ({ id, trackData }) => ({
    type: types.FETCH_POST,
    payload: {
      id,
      trackData,
    },
  }),
  fetchPostSuccess: ({ post, relatedPosts, authorAvatar }) => ({
    type: types.FETCH_POST_SUCCESS,
    payload: {
      post,
      relatedPosts,
      authorAvatar,
    },
  }),
  fetchPostFailure: () => ({ type: types.FETCH_POST_FAILURE }),
  resetPost: () => ({ type: types.RESET_POST }),
  toggleFavorite: () => ({ type: types.TOGGLE_FAVORITE }),
  showLoginDialog: () => ({ type: types.SHOW_LOGIN_DIALOG }),
  hideLoginDialog: () => ({ type: types.HIDE_LOGIN_DIALOG }),
  postEvent: trackData => ({
    type: types.POST_EVENT,
    payload: {
      trackData,
    },
  }),
  fetchQuizHistory: () => ({ type: types.FETCH_QUIZ_HISTORY }),
  fetchQuizHistorySuccess: ({ quizzes, hasNextQuiz }) => ({
    type: types.FETCH_QUIZ_HISTORY_SUCCESS,
    payload: {
      quizzes,
      hasNextQuiz,
    },
  }),
  fetchQuizHistoryFailure: () => ({ type: types.FETCH_QUIZ_HISTORY_FAILURE }),
  fetchNextQuiz: () => ({ type: types.FETCH_NEXT_QUIZ }),
  fetchNextQuizSuccess: ({ quiz, hasNextQuiz }) => ({
    type: types.FETCH_NEXT_QUIZ_SUCCESS,
    payload: {
      quiz,
      hasNextQuiz,
    },
  }),
  fetchNextQuizFailure: () => ({ type: types.FETCH_NEXT_QUIZ_FAILURE }),
  changeConcept: id => ({
    type: types.CHANGE_CONCEPT,
    payload: {
      id,
    },
  }),
  postQuizAnswer: ({ answer, isCorrect }) => ({
    type: types.POST_QUIZ_ANSWER,
    payload: {
      answer,
      isCorrect,
    },
  }),
  postQuizAnswerSuccess: () => ({ type: types.POST_QUIZ_ANSWER_SUCESS }),
  changeQuizIndex: index => ({
    type: types.CHANGE_QUIZ_INDEX,
    payload: {
      index,
    },
  }),
  changeQuizIndexSuccess: (quizzes, index) => ({
    type: types.CHANGE_QUIZ_INDEX_SUCCESS,
    payload: {
      quizzes,
      index,
    },
  }),
};

export const INIT_STATE = {
  post: {},
  relatedPosts: [],
  authorAvatar: '',
  status: LoadingState.EMPTY,
  isLoginDialogOpen: false,
  quizzes: [],
  selectedConceptId: '',
  hasNextQuiz: false,
  currentQuizIndex: -1,
};

const reducer = (state = INIT_STATE, action) => {
  switch (action.type) {
    case types.FETCH_POST:
      return {
        ...state,
        status: LoadingState.LOADING,
      };
    case types.FETCH_POST_SUCCESS:
      return {
        ...state,
        status: LoadingState.READY,
        post: action.payload.post,
        relatedPosts: action.payload.relatedPosts,
        authorAvatar: action.payload.authorAvatar,
        quizzes: INIT_STATE.quizzes,
        hasNextQuiz: INIT_STATE.hasNextQuiz,
        currentQuizIndex: INIT_STATE.currentQuizIndex,
      };
    case types.FETCH_POST_FAILURE:
      return {
        ...state,
        status: LoadingState.ERROR,
      };
    case types.RESET_POST:
      return {
        ...INIT_STATE,
      };
    case types.TOGGLE_FAVORITE:
      return {
        ...state,
        post: {
          ...state.post,
          isLike: !state.post.isLike,
        },
      };
    case types.SHOW_LOGIN_DIALOG:
      return {
        ...state,
        isLoginDialogOpen: true,
      };
    case types.HIDE_LOGIN_DIALOG:
      return {
        ...state,
        isLoginDialogOpen: false,
      };
    case types.CHANGE_CONCEPT:
      return {
        ...state,
        selectedConceptId: action.payload.id,
      };
    case types.FETCH_QUIZ_HISTORY_SUCCESS: {
      const { quizzes, hasNextQuiz } = action.payload;

      return {
        ...state,
        quizzes,
        hasNextQuiz,
        currentQuizIndex: quizzes.length - 1,
      };
    }
    case types.FETCH_NEXT_QUIZ_SUCCESS: {
      const { quiz, hasNextQuiz } = action.payload;
      const quizzes = state.quizzes;
      if (!!quiz) {
        quizzes.push(quiz);
      }

      return {
        ...state,
        quizzes,
        hasNextQuiz,
        currentQuizIndex: quizzes.length - 1,
      };
    }
    case types.CHANGE_QUIZ_INDEX:
      return {
        ...state,
        status: LoadingState.LOADING,
      };
    case types.CHANGE_QUIZ_INDEX_SUCCESS:
      return {
        ...state,
        status: LoadingState.READY,
        quizzes: action.payload.quizzes,
        currentQuizIndex: action.payload.index,
      };
    case types.POST_QUIZ_ANSWER: {
      const { answer, isCorrect } = action.payload;
      const quiz = state.quizzes[state.currentQuizIndex];
      quiz.answer = answer;
      quiz.status = isCorrect ? quizStatus.PASS : quizStatus.FAIL;

      return {
        ...state,
        status: LoadingState.LOADING,
      };
    }
    case types.POST_QUIZ_ANSWER_SUCESS:
      return {
        ...state,
        status: LoadingState.READY,
      };
    default:
      return state;
  }
};

export default reducer;

export const getPost = state => state.post.post;
export const getAuthorAvatar = state => state.post.authorAvatar;
export const getStatus = state => state.post.status;
export const getPostInfo = (state, property) => state.post.post[property];
export const getLoginDialogOpen = state => state.post.isLoginDialogOpen;
export const getPostId = state => getPostInfo(state, 'id');
export const getVisitCount = state => getPostInfo(state, 'visitCount');
export const getAuthorName = state => getPostInfo(state, 'authorName');
export const getAuthorIntro = state => getPostInfo(state, 'authorIntro');
export const getPublishDate = state => getPostInfo(state, 'publishDate');
export const getCurriculumName = state => getPostInfo(state, 'curriculumName');
export const getSubjectName = state => getPostInfo(state, 'subjectName');
export const getSubjectId = state => getPostInfo(state, 'subjectId');
export const getTopicName = state => getPostInfo(state, 'topicName');
export const getSubtopicName = state => getPostInfo(state, 'subtopicName');
export const getSubtopicId = state => getPostInfo(state, 'subtopicId');
export const getConcepts = state => getPostInfo(state, 'concepts');
export const getRelatedPosts = state => state.post.relatedPosts;
export const getQuizzes = state => state.post.quizzes;
export const getSelectedConceptId = state => state.post.selectedConceptId;
export const getCurrentQuizIndex = state => state.post.currentQuizIndex;
export const getHasNextQuiz = state => state.post.hasNextQuiz;
export const getCurrentQuiz = createSelector(
  getQuizzes,
  getCurrentQuizIndex,
  (quizzes, index) => quizzes[index]
);
export const getSelectedConceptIndex = createSelector(
  getConcepts,
  getSelectedConceptId,
  (concepts, id) => concepts.findIndex(c => c.qms_id === id)
);

export const transferAuthorAvatar = ({ author }) => ({
  profilePictureUrl: author.profile_pic_url,
});



// WEBPACK FOOTER //
// ./app/redux/post.js